import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import TeamworkGraphIcon from '@atlaskit/icon-lab/core/teamwork-graph';
import AiGenerativeTextSummaryIcon from '@atlaskit/icon/core/ai-generative-text-summary';
import ClockIcon from '@atlaskit/icon/core/clock';
import CommentIcon from '@atlaskit/icon/core/comment';
import ListBulletedIcon from '@atlaskit/icon/core/list-bulleted';
import WorkItemsIcon from '@atlaskit/icon/core/work-items';
import { type IconProps } from '@atlaskit/icon/types';

import { useDynamicSurfacePanel } from '@atlassian/ai-smart-button';
import type { AIActionType, BaseAIAction, BaseNudge } from '@atlassian/ai-smart-button-api';
import { useSourceAIActions } from '@atlassian/ai-smart-button-api';

import { FooterCommentsSummaryCombined } from '@confluence/ai-comments-summary/entry-points/FooterCommentsSummaryCombined';
import { CommentsSummarySource } from '@confluence/ai-comments-summary/entry-points/CommentsSummaryContent';
import { useAutomationPermissionService } from '@confluence/automation-menu/entry-points/AutomationPermissionService';
import { PageAutomationRovoButton } from '@confluence/automation-menu/entry-points/PageAutomationRovoButton';
import {
	CommentCountsContext,
	CommentCountsProvider,
} from '@confluence/comment-context/entry-points/CommentCountsContext';
import { ExploreRelatedWork } from '@confluence/explore-related-work';
import { fg } from '@confluence/feature-gating';
import {
	useWarningDialogStateActions,
	WarningDialog,
} from '@confluence/issue-create-side-panel/entry-points/IssueCreateWarningDialogContextProvider';
import { BULK_ISSUE_CREATE_SIDE_PANEL_ID } from '@confluence/issue-create-side-panel/entry-points/BulkCreateSidePanel';
import { useObjectSidebarActions } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar';
import { PageCatchupComponent } from '@confluence/page-catchup/entry-points/PageCatchupStreamingEntryPoint';
import { FilterComponent } from '@confluence/page-catchup/entry-points/FilterComponent';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { QuickSummaryStreamingComponent } from '@confluence/quick-summary/entry-points/QuickSummaryStreamingEntryPoint';
import { QuickSummarySaveTimeText } from '@confluence/quick-summary/entry-points/QuickSummarySaveTimeText';

import { CatchupContentType } from '../__types__/CatchupLastViewedTimeQuery';

import { useBulkIssueCreate } from './useBulkIssueCreate';
import { useCreateObjectSidebarProp } from './useCreateObjectSidebarProp';
import { useDynamicSplitButton } from './useDynamicSplitButton';

export const i18n = defineMessages({
	inlineCommentsMenuItemLabel: {
		id: 'ai-floating-context-menu.menu.item.label.inline-comments',
		defaultMessage: 'Summarize comments',
		description: 'Label for the AI Content Actions Inline Comments Menu Item',
	},
	pageCatchupMenuItemLabel: {
		id: 'ai-floating-context-menu.menu.item.label.page-catchup',
		defaultMessage: 'Summarize changes',
		description: 'Label for the AI Content Actions Page Catchup Menu Item',
	},
	summarizeSectionLabel: {
		id: 'ai-floating-context-menu.summarize.section.label',
		defaultMessage: 'Summarize page',
		description: 'Label for the summarize section in the menu group',
	},
	bulkIssueCreateMenuItemLabel: {
		id: 'ai-floating-context-menu.menu.item.label.bulk-issue-create',
		defaultMessage: 'Create Jira issues',
		description: 'Label for the Jira Bulk Issue Create Menu Item',
	},
	bulkWorkItemsCreateMenuItemLabel: {
		id: 'ai-floating-context-menu.menu.item.label.bulk-work-items-create',
		defaultMessage: 'Create Jira work items',
		description: 'Label for the Jira Bulk Work Items Create Menu Item',
	},
	exploreRelatedWorkAndUpdates: {
		id: 'ai-floating-context-menu.menu.item.label.explore-related-items.non-final',
		defaultMessage: 'Explore related work and resources',
		description: 'Label for the Explore Related Items Menu Item',
	},
});

const VIEW_PAGE_SOURCE_ID = 'view-page';

type AIConsumptionViewPageProps = {
	contentId: string;
	contentType: string;
	spaceKey: string;
	contentTitle: string;
	lastModifiedDate?: string | null;
};

export const useAIConsumptionViewPage = ({
	contentId,
	contentType,
	spaceKey,
	contentTitle,
	lastModifiedDate,
}: AIConsumptionViewPageProps) => {
	const { formatMessage } = useIntl();
	const { setActions, setNudges, setAutomationsConfig } = useSourceAIActions(VIEW_PAGE_SOURCE_ID);
	const { unresolvedInlineCommentsCount, footerCommentsCount } = useContext(CommentCountsContext);
	const { showObjectSidebar } = useObjectSidebarActions();
	const pageMode = useGetPageMode();
	const { canManageAutomations } = useAutomationPermissionService(spaceKey);

	const objectSidebar = useCreateObjectSidebarProp();
	const dynamicSurfacePanel = useDynamicSurfacePanel(objectSidebar);
	const rightSidebarContext = useRightSidebarContext();
	const { showWarningDialog } = useWarningDialogStateActions();
	const contentTypeEnum =
		contentType === 'page' ? CatchupContentType.PAGE : CatchupContentType.BLOGPOST;
	const { showCatchupAsDefault, showSummarizeAsDefault, readTime } = useDynamicSplitButton(
		contentId,
		contentTypeEnum,
	);
	const isSidePanelOpen =
		rightSidebarContext.getCurrent()?.panelId === BULK_ISSUE_CREATE_SIDE_PANEL_ID;

	const content = useRef<HTMLDivElement | null>(
		typeof document !== 'undefined' ? document.querySelector('div#content') : null,
	);

	const handleBulkIssueCreate = useBulkIssueCreate({
		contentId,
		content,
	});

	const pageCatchupAction = useMemo(
		() => ({
			id: fg('aix_nudge_recommendation_migration_enabled')
				? 'content-catchup'
				: 'summarize-changes-ai-action',
			title: formatMessage(i18n.pageCatchupMenuItemLabel),
			icon: <ClockIcon label="" />,
			onClick: () => showObjectSidebar(dynamicSurfacePanel, 'push'),
			viewConfig: {
				render: () => (
					<PageCatchupComponent
						contentId={contentId}
						contentType={contentType}
						spaceKey={spaceKey}
						setIsOpen={() => {}}
						contentPath={window.location.pathname}
						isInObjectSidebar
					/>
				),
				headerRightAlignedElement: fg('cc_ai_page_catchup_filtering') ? (
					<FilterComponent />
				) : undefined,
			},
			type: 'ai-action' as AIActionType,
		}),
		[formatMessage, contentId, contentType, spaceKey, dynamicSurfacePanel, showObjectSidebar],
	);

	const quickSummaryAction = useMemo(
		() => ({
			id: fg('aix_nudge_recommendation_migration_enabled')
				? 'summarize'
				: 'summarize-page-ai-action',
			title: formatMessage(i18n.summarizeSectionLabel),
			icon: <ListBulletedIcon label="" />,
			onClick: () => showObjectSidebar(dynamicSurfacePanel, 'push'),
			description: (
				<QuickSummarySaveTimeText originalReadTime={readTime ?? 0} summaryReadTime={1} />
			),
			viewConfig: {
				render: () => (
					<QuickSummaryStreamingComponent
						contentId={contentId}
						spaceKey={spaceKey}
						contentType={contentType}
						lastModifiedDate={lastModifiedDate ?? ''}
						source="view-page"
						setIsOpen={() => {}}
						contentPath={window.location.pathname}
						isInObjectSidebar
					/>
				),
			},
			type: 'ai-action' as AIActionType,
		}),
		[
			formatMessage,
			contentId,
			contentType,
			spaceKey,
			lastModifiedDate,
			dynamicSurfacePanel,
			showObjectSidebar,
			readTime,
		],
	);

	const nudges = useMemo(() => {
		const baseNudges: BaseNudge[] = [
			{
				...pageCatchupAction,
				nudgeIcon: (_: IconProps) => <ClockIcon label="" testId="content-catchup.nudge-icon" />,
			},
			{
				...quickSummaryAction,
				nudgeIcon: (_: IconProps) => (
					<AiGenerativeTextSummaryIcon label="" testId="summarize.nudge-icon" />
				),
			},
		];
		return baseNudges;
	}, [pageCatchupAction, quickSummaryAction]);

	const onBulkIssueCreate = useCallback(() => {
		if (isSidePanelOpen) {
			showWarningDialog(WarningDialog.FLOATING);
		} else {
			void handleBulkIssueCreate();
		}
	}, [isSidePanelOpen, showWarningDialog, handleBulkIssueCreate]);

	const actions = useMemo(() => {
		const baseActions: BaseAIAction[] = [quickSummaryAction, pageCatchupAction];

		if (unresolvedInlineCommentsCount > 0 || footerCommentsCount > 0) {
			baseActions.push({
				id: 'summarize-comments-ai-action',
				title: formatMessage(i18n.inlineCommentsMenuItemLabel),
				icon: <CommentIcon label="" />,
				viewConfig: {
					render: () => (
						<CommentCountsProvider contentId={contentId}>
							<FooterCommentsSummaryCombined
								contentId={contentId}
								contentType={contentType}
								source={CommentsSummarySource.Footer}
								spaceKey={spaceKey}
								isInObjectSidebar
							/>
						</CommentCountsProvider>
					),
				},
				type: 'ai-action' as AIActionType,
			});
		}

		if (pageMode === PageMode.VIEW && fg('confluence_ai_bulk_issue_creation')) {
			baseActions.push({
				id: 'bulk-issue-create-ai-action',
				title: formatMessage(
					fg('confluence-issue-terminology-refresh')
						? i18n.bulkWorkItemsCreateMenuItemLabel
						: i18n.bulkIssueCreateMenuItemLabel,
				),
				icon: <WorkItemsIcon label="" />,
				onClick: onBulkIssueCreate,
				type: 'ai-action' as AIActionType,
			});
		}

		if (fg('platform-linking-confluence-teamwork-context-panel')) {
			baseActions.unshift({
				id: 'explore-related-work',
				title: formatMessage(i18n.exploreRelatedWorkAndUpdates),
				icon: <TeamworkGraphIcon label="" />,
				viewConfig: {
					render: () => <ExploreRelatedWork contentId={contentId} contentTitle={contentTitle} />,
				},
				type: 'ai-action' as AIActionType,
			});
		}

		return baseActions;
	}, [
		formatMessage,
		contentId,
		contentTitle,
		spaceKey,
		contentType,
		unresolvedInlineCommentsCount,
		footerCommentsCount,
		quickSummaryAction,
		pageCatchupAction,
		onBulkIssueCreate,
		pageMode,
	]);

	useEffect(() => {
		setActions(actions);
		if (fg('aix_nudge_recommendation_migration_enabled')) {
			setNudges(nudges);
		}

		if (contentType === 'page' && fg('confluence_ai_automations_in_rovo_button')) {
			setAutomationsConfig({
				userAutomations: {
					render: () => (
						<PageAutomationRovoButton
							spaceKey={spaceKey}
							pageId={contentId}
							contentTitle={contentTitle}
						/>
					),
				},
				...(canManageAutomations && {
					adminAutomations: {
						onClick: () =>
							window.open(`/wiki/spaces/${spaceKey}/settings/automation#rule/new`, '_blank'),
					},
				}),
			});
		}

		if (showSummarizeAsDefault && !fg('aix_nudge_recommendation_migration_enabled')) {
			setNudges([
				{
					...quickSummaryAction,
					nudgeIcon: (_: IconProps) => <AiGenerativeTextSummaryIcon label="" />,
				},
			]);
		}

		if (showCatchupAsDefault && !fg('aix_nudge_recommendation_migration_enabled')) {
			setNudges([{ ...pageCatchupAction, nudgeIcon: (_: IconProps) => <ClockIcon label="" /> }]);
		}
	}, [
		actions,
		nudges,
		pageCatchupAction,
		setActions,
		setNudges,
		showCatchupAsDefault,
		setAutomationsConfig,
		contentType,
		spaceKey,
		contentId,
		contentTitle,
		canManageAutomations,
		quickSummaryAction,
		showSummarizeAsDefault,
	]);
};
